import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from 'lucide-react';
import { formatDateWithPaddedDay } from '@/utils/helpers';
import { MarkFrequency } from '@/services/launch-config.service';

interface ConfigFieldProps {
  label: string;
  value: string;
  editing: boolean;
  error?: string;
  required?: boolean;
  type?: 'text' | 'date' | 'select';
  placeholder?: string;
  onChange: (value: string) => void;
  selectOptions?: Array<{ value: string; label: string }>;
  displayValue?: string;
}

export function ConfigField({
  label,
  value,
  editing,
  error,
  required,
  type = 'text',
  placeholder,
  onChange,
  selectOptions,
  displayValue,
}: ConfigFieldProps) {
  const renderInput = () => {
    if (type === 'date') {
      return <DatePicker value={value} onChange={onChange} />;
    }

    if (type === 'select') {
      return (
        <Select
          value={value}
          onValueChange={(v: MarkFrequency | '__none__') => onChange(v)}
        >
          <SelectTrigger error={error}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {selectOptions?.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    );
  };

  const renderDisplay = () => {
    if (type === 'date') {
      return (
        <div className="flex items-center gap-2 min-h-[2.5rem]">
          <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <span className="text-lg font-semibold text-blue-900">
            {displayValue || formatDateWithPaddedDay(value) || '—'}
          </span>
        </div>
      );
    }

    return (
      <div className="text-lg font-semibold text-blue-900 min-h-[2.5rem] flex items-center">
        {displayValue || (value === '__none__' ? '—' : value) || '—'}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <Label
        required={required}
        className="text-sm font-medium text-blue-700 mb-2 block leading-5 min-h-[1.25rem]"
      >
        {label}
      </Label>
      <div className="flex items-center min-h-[2.5rem]">
        {editing ? renderInput() : renderDisplay()}
      </div>
      {error && <p className="text-sm text-red-600 mt-1 min-h-[1.25rem]">{error}</p>}
    </div>
  );
}
